import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { cashflow } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  UploadCloud, Wallet, TrendingUp, TrendingDown, IndianRupee,
  ChevronLeft, ChevronRight, Search, Plus, X, ArrowRight, Pencil, Trash2, AlertTriangle,
} from 'lucide-react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface MonthlyRow {
  year: number;
  month: number;
  opening_balance: number;
  closing_balance: number;
  total_diff: number;
  total_credits: number;
  total_debits: number;
  personal_outflow: number;
  transfer_net: number;
  exclude_net: number;
  unclassified_count: number;
  unclassified_amount: number;
  business_net_cash_flow: number;
  reconciled: boolean;
}

interface Transaction {
  id: number;
  date: string;
  narration: string;
  debit_amount: number;
  credit_amount: number;
  balance: number;
  classification: string;
}

const CLASSIFICATIONS: { value: string; label: string; color: string; soft: string }[] = [
  { value: 'business', label: 'Business', color: 'var(--accent-blue)', soft: 'var(--accent-blue-soft)' },
  { value: 'personal', label: 'Personal', color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)' },
  { value: 'transfer', label: 'Transfer', color: 'var(--accent-purple)', soft: 'var(--accent-purple-soft)' },
  { value: 'exclude', label: 'Exclude', color: 'var(--text-muted)', soft: 'var(--bg-elevated)' },
  { value: 'unclassified', label: 'Unclassified', color: 'var(--accent-red)', soft: 'var(--accent-red-soft)' },
];
const classificationMeta = (value: string) => CLASSIFICATIONS.find(c => c.value === value) || CLASSIFICATIONS[0];

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl)', padding: 24,
};

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const monthKey = (m: MonthlyRow) => `${m.year}-${m.month}`;
const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtSigned = (n: number) => `${n >= 0 ? '+' : ''}₹${fmt(n)}`;

const MiniCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; softColor: string;
}> = ({ label, value, icon, color, softColor }) => (
  <div style={{
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '18px 20px',
    display: 'flex', alignItems: 'center', gap: 14,
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: softColor,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0,
    }}>{icon}</div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5 }}>{value}</div>
    </div>
  </div>
);

const ClassificationSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const meta = classificationMeta(value);
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{
        padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
        border: 'none', background: meta.soft, color: meta.color, cursor: 'pointer',
      }}
    >
      {CLASSIFICATIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
    </select>
  );
};

const CashFlowTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].value as number;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '10px 14px', boxShadow: 'var(--shadow-elevated)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: v >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
        {fmtSigned(v)}
      </div>
    </div>
  );
};

interface EditDraft {
  date: string; narration: string; debit_amount: string; credit_amount: string; balance: string;
}

const CashFlow: React.FC = () => {
  const { toast, confirm } = useToast();
  const [months, setMonths] = useState<MonthlyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const [txnsByMonth, setTxnsByMonth] = useState<Record<string, Transaction[]>>({});
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [classificationFilter, setClassificationFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({ date: '', narration: '', debit_amount: '', credit_amount: '', balance: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refreshMonthly = async () => {
    try {
      const res = await cashflow.getMonthly();
      setMonths(res.data);
      return res.data as MonthlyRow[];
    } catch (e: any) {
      toast(e.response?.data?.detail || 'Failed to load cash flow data', 'error');
      return [];
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await refreshMonthly();
      if (data.length > 0) setSelectedIdx(data.length - 1);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMonth = selectedIdx !== null ? months[selectedIdx] : null;
  const prevMonth = selectedIdx !== null && selectedIdx > 0 ? months[selectedIdx - 1] : null;
  const key = selectedMonth ? monthKey(selectedMonth) : null;

  const refetchCurrentMonthTransactions = async () => {
    if (!selectedMonth || !key) return;
    const res = await cashflow.getTransactions(selectedMonth.year, selectedMonth.month);
    setTxnsByMonth(prev => ({ ...prev, [key]: res.data }));
  };

  useEffect(() => {
    if (!key || txnsByMonth[key]) return;
    setTxnsLoading(true);
    cashflow.getTransactions(selectedMonth!.year, selectedMonth!.month)
      .then((res: any) => setTxnsByMonth(prev => ({ ...prev, [key]: res.data })))
      .catch((e: any) => toast(e.response?.data?.detail || 'Failed to load transactions', 'error'))
      .finally(() => setTxnsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    setSearch('');
    setSelected(new Set());
    setClassificationFilter(null);
    setEditingId(null);
  }, [key]);

  const txns = (key && txnsByMonth[key]) || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = txns;
    if (classificationFilter) rows = rows.filter(t => t.classification === classificationFilter);
    if (q) rows = rows.filter(t => t.narration.toLowerCase().includes(q));
    return rows;
  }, [txns, search, classificationFilter]);
  const filteredIds = filtered.map(t => t.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));

  const setClassification = async (txn: Transaction, classification: string) => {
    if (!key) return;
    const prevValue = txn.classification;
    setTxnsByMonth(prev => ({ ...prev, [key]: prev[key].map(t => t.id === txn.id ? { ...t, classification } : t) }));
    try {
      await cashflow.updateTransaction(txn.id, { classification });
      await refreshMonthly();
    } catch (e: any) {
      setTxnsByMonth(prev => ({ ...prev, [key]: prev[key].map(t => t.id === txn.id ? { ...t, classification: prevValue } : t) }));
      toast(e.response?.data?.detail || 'Failed to update transaction', 'error');
    }
  };

  const toggleSelected = (id: number) => {
    setSelected(prev => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id); else set.add(id);
      return set;
    });
  };

  const toggleSelectAll = () => {
    setSelected(prev => {
      const allSelected = filteredIds.length > 0 && filteredIds.every(id => prev.has(id));
      return allSelected ? new Set<number>() : new Set(filteredIds);
    });
  };

  const bulkSetClassification = async (classification: string) => {
    if (!key || selected.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const prevRows = txnsByMonth[key];
    setTxnsByMonth(prev => ({ ...prev, [key]: prev[key].map(t => ids.includes(t.id) ? { ...t, classification } : t) }));
    try {
      await cashflow.setClassificationBulk(ids, classification);
      await refreshMonthly();
      setSelected(new Set());
      toast(`${ids.length} transaction${ids.length > 1 ? 's' : ''} marked ${classificationMeta(classification).label}`, 'success');
    } catch (e: any) {
      setTxnsByMonth(prev => ({ ...prev, [key]: prevRows }));
      toast(e.response?.data?.detail || 'Failed to update transactions', 'error');
    }
    setBulkBusy(false);
  };

  const bulkDelete = async () => {
    if (!key || selected.size === 0) return;
    const ok = await confirm(`Delete ${selected.size} selected transaction${selected.size > 1 ? 's' : ''}? This cannot be undone.`);
    if (!ok) return;
    setBulkBusy(true);
    try {
      await cashflow.deleteTransactionsBulk(Array.from(selected));
      await refetchCurrentMonthTransactions();
      await refreshMonthly();
      setSelected(new Set());
      toast('Transactions deleted', 'success');
    } catch (e: any) {
      toast(e.response?.data?.detail || 'Failed to delete transactions', 'error');
    }
    setBulkBusy(false);
  };

  const startEdit = (t: Transaction) => {
    setEditingId(t.id);
    setEditDraft({
      date: t.date, narration: t.narration,
      debit_amount: t.debit_amount ? String(t.debit_amount) : '',
      credit_amount: t.credit_amount ? String(t.credit_amount) : '',
      balance: String(t.balance),
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (t: Transaction) => {
    setSavingEdit(true);
    try {
      await cashflow.updateTransaction(t.id, {
        date: editDraft.date,
        narration: editDraft.narration,
        debit_amount: parseFloat(editDraft.debit_amount) || 0,
        credit_amount: parseFloat(editDraft.credit_amount) || 0,
        balance: parseFloat(editDraft.balance),
      });
      await refetchCurrentMonthTransactions();
      await refreshMonthly();
      setEditingId(null);
      toast('Transaction updated', 'success');
    } catch (e: any) {
      toast(e.response?.data?.detail || 'Failed to update transaction', 'error');
    }
    setSavingEdit(false);
  };

  const deleteOne = async (t: Transaction) => {
    const ok = await confirm(`Delete this transaction — "${t.narration}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await cashflow.deleteTransaction(t.id);
      await refetchCurrentMonthTransactions();
      await refreshMonthly();
      toast('Transaction deleted', 'success');
    } catch (e: any) {
      toast(e.response?.data?.detail || 'Failed to delete transaction', 'error');
    }
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await cashflow.import(file);
      toast(`${res.data.added} row(s) added, ${res.data.skipped} duplicate(s) skipped`, 'success');
      const data = await refreshMonthly();
      setSelectedIdx(data.length > 0 ? data.length - 1 : null);
    } catch (e: any) {
      toast(e.response?.data?.detail || 'Failed to parse statement', 'error');
    }
    setUploading(false);
  };

  const UploadPanel = (
    <div style={{ ...cardStyle, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>Import Bank Statement</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 440 }}>
            Upload a full HDFC statement (XLS/XLSX) — deposits, withdrawals and running balance.
            Re-uploading overlapping statements is safe; duplicate rows are skipped automatically.
          </div>
        </div>
        {months.length > 0 && (
          <button onClick={() => setShowUpload(false)} style={{
            background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
          }}><X size={15} color="var(--text-muted)" /></button>
        )}
      </div>
      <label
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={e => {
          e.preventDefault();
          setDragActive(false);
          const f = e.dataTransfer.files[0];
          if (f) handleFile(f);
        }}
        style={{
          display: 'block', border: `2px dashed ${dragActive ? 'var(--accent-green)' : 'var(--border)'}`,
          borderRadius: 10, padding: '36px 24px', textAlign: 'center', cursor: 'pointer',
          transition: 'border-color 0.15s', maxWidth: 480,
        }}
      >
        <input
          type="file" accept=".xls,.xlsx" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
        <UploadCloud size={26} color="var(--text-muted)" style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {uploading ? 'Importing…' : 'Drop the statement file here, or click to browse'}
        </div>
      </label>
    </div>
  );

  if (loading) {
    return <Layout title="Cash Flow"><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div></Layout>;
  }

  if (months.length === 0) {
    return (
      <Layout title="Cash Flow" subtitle="Import a statement to see your monthly cash position">
        {UploadPanel}
      </Layout>
    );
  }

  const m = selectedMonth!;
  const netPositive = m.business_net_cash_flow >= 0;
  const delta = prevMonth ? m.business_net_cash_flow - prevMonth.business_net_cash_flow : null;

  const strip: { label: string; amount: number; op: '' | '+' | '−'; filter: string | null }[] = [
    { label: 'Opening', amount: m.opening_balance, op: '', filter: null },
    { label: 'Business', amount: m.business_net_cash_flow, op: m.business_net_cash_flow >= 0 ? '+' : '−', filter: 'business' },
    { label: 'Personal', amount: m.personal_outflow, op: '−', filter: 'personal' },
    { label: 'Transfers', amount: m.transfer_net, op: m.transfer_net >= 0 ? '−' : '+', filter: 'transfer' },
    { label: 'Excluded', amount: m.exclude_net, op: m.exclude_net >= 0 ? '−' : '+', filter: 'exclude' },
    { label: 'Closing', amount: m.closing_balance, op: '', filter: null },
  ];

  return (
    <Layout title="Cash Flow" subtitle="Monthly business cash position, excluding personal spending">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '4px 6px',
        }}>
          <button
            onClick={() => setSelectedIdx(i => Math.max(0, (i ?? 0) - 1))}
            disabled={selectedIdx === 0}
            style={{
              background: 'var(--bg-elevated)', border: 'none', borderRadius: 6, width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: selectedIdx === 0 ? 'default' : 'pointer', opacity: selectedIdx === 0 ? 0.4 : 1,
            }}
          ><ChevronLeft size={16} color="var(--text-secondary)" /></button>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', padding: '0 14px', minWidth: 150, textAlign: 'center' }}>
            {MONTH_NAMES[m.month]} {m.year}
          </div>
          <button
            onClick={() => setSelectedIdx(i => Math.min(months.length - 1, (i ?? 0) + 1))}
            disabled={selectedIdx === months.length - 1}
            style={{
              background: 'var(--bg-elevated)', border: 'none', borderRadius: 6, width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: selectedIdx === months.length - 1 ? 'default' : 'pointer',
              opacity: selectedIdx === months.length - 1 ? 0.4 : 1,
            }}
          ><ChevronRight size={16} color="var(--text-secondary)" /></button>
        </div>

        <button onClick={() => setShowUpload(v => !v)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
          background: showUpload ? 'var(--bg-elevated)' : 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          <Plus size={14} /> Import Statement
        </button>
      </div>

      {showUpload && UploadPanel}

      <div style={{
        ...cardStyle, marginBottom: 16, position: 'relative', overflow: 'hidden',
        background: netPositive ? 'var(--accent-green-soft)' : 'var(--accent-red-soft)',
        border: `1px solid ${netPositive ? 'rgba(45,212,160,0.25)' : 'rgba(239,90,111,0.25)'}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700,
          color: netPositive ? 'var(--accent-green)' : 'var(--accent-red)',
          textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12,
        }}>
          {netPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {netPositive ? 'Cash flow generated' : 'Cash flow lost'} in {MONTH_NAMES[m.month]}
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -1.5, lineHeight: 1 }}>
          {fmtSigned(m.business_net_cash_flow)}
        </div>
        {delta !== null && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '4px 10px',
            borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'var(--bg-card)', color: 'var(--text-secondary)',
          }}>
            {delta >= 0 ? <TrendingUp size={12} color="var(--accent-green)" /> : <TrendingDown size={12} color="var(--accent-red)" />}
            {fmtSigned(delta)} vs {MONTH_NAMES[prevMonth!.month].slice(0, 3)}
          </div>
        )}
      </div>

      <div style={{
        ...cardStyle, padding: '14px 20px', marginBottom: 16, display: 'flex',
        alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5,
      }}>
        {strip.map((seg, i) => (
          <React.Fragment key={seg.label}>
            <button
              onClick={() => seg.filter && setClassificationFilter(prev => prev === seg.filter ? null : seg.filter)}
              disabled={!seg.filter}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                padding: '6px 10px', borderRadius: 8, border: 'none', cursor: seg.filter ? 'pointer' : 'default',
                background: classificationFilter === seg.filter && seg.filter ? 'var(--bg-elevated)' : 'transparent',
              }}
            >
              <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>{seg.label}</span>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                {seg.op}₹{fmt(Math.abs(seg.amount))}
              </span>
            </button>
            {i < strip.length - 1 && <ArrowRight size={12} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
          </React.Fragment>
        ))}
        {!m.reconciled && (
          <span style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
            borderRadius: 20, background: 'var(--accent-amber-soft)', color: 'var(--accent-amber)',
            fontSize: 11.5, fontWeight: 600,
          }} title="Closing balance doesn't match the sum of this month's credits and debits — check for a missing or duplicate row.">
            <AlertTriangle size={12} /> Doesn't reconcile
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <MiniCard
          label="Opening Balance" value={`₹${fmt(m.opening_balance)}`}
          icon={<Wallet size={16} />} color="var(--accent-blue)" softColor="var(--accent-blue-soft)"
        />
        <MiniCard
          label="Closing Balance" value={`₹${fmt(m.closing_balance)}`}
          icon={<ArrowRight size={16} />} color="var(--accent-blue)" softColor="var(--accent-blue-soft)"
        />
        <MiniCard
          label="Personal Outflow" value={`₹${fmt(m.personal_outflow)}`}
          icon={<IndianRupee size={16} />} color="var(--accent-amber)" softColor="var(--accent-amber-soft)"
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Net Cash Flow by Month
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Click a bar to jump to that month
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={months.map(mm => ({
            name: `${MONTH_NAMES[mm.month].slice(0, 3)} '${String(mm.year).slice(2)}`,
            value: mm.business_net_cash_flow,
          }))}>
            <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip content={<CashFlowTooltip />} cursor={{ fill: 'var(--bg-elevated)' }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(_, idx) => setSelectedIdx(idx)}>
              {months.map((mm, i) => {
                const isSel = i === selectedIdx;
                const base = mm.business_net_cash_flow >= 0 ? 'var(--accent-green)' : 'var(--accent-red)';
                return <Cell key={i} fill={base} fillOpacity={isSel ? 1 : 0.35} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {m.unclassified_count > 0 && (
        <div style={{
          ...cardStyle, padding: '10px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.25)',
        }}>
          <AlertTriangle size={14} color="var(--accent-red)" />
          <span style={{ fontSize: 12.5, color: 'var(--text-primary)', flex: 1 }}>
            {m.unclassified_count} unclassified transaction{m.unclassified_count > 1 ? 's' : ''} (₹{fmt(m.unclassified_amount)}) — currently counted as business.
          </span>
          <button
            onClick={() => setClassificationFilter('unclassified')}
            style={{
              padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: 'none', background: 'var(--accent-red)', color: '#fff',
            }}
          >Review</button>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginRight: 'auto' }}>
            Transactions — {MONTH_NAMES[m.month]} {m.year}
          </div>
          {classificationFilter && (
            <button onClick={() => setClassificationFilter(null)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 20,
              border: 'none', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
            }}>
              {classificationMeta(classificationFilter).label} <X size={11} />
            </button>
          )}
          <div style={{ position: 'relative', flex: '0 0 240px' }}>
            <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search narration…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '7px 10px 7px 30px', fontSize: 12.5,
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                background: 'var(--bg-input)', color: 'var(--text-primary)',
              }}
            />
          </div>
          {selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)' }}>
              <span>{selected.size} selected</span>
              <select
                disabled={bulkBusy}
                defaultValue=""
                onChange={e => { if (e.target.value) { bulkSetClassification(e.target.value); e.target.value = ''; } }}
                style={{
                  padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                }}
              >
                <option value="" disabled>Set classification…</option>
                {CLASSIFICATIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button
                disabled={bulkBusy}
                onClick={bulkDelete}
                style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: 'none', background: 'var(--accent-red-soft)', color: 'var(--accent-red)',
                }}
              >Delete</button>
            </div>
          )}
        </div>
        {txnsLoading ? (
          <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-muted)' }}>Loading transactions…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'center', width: 32 }}>
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} />
                  </th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'left', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Date</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'left', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Narration</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'right', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Debit</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'right', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Credit</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'right', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Balance</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'center', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Classification</th>
                  <th style={{ padding: '10px 16px', color: 'var(--text-muted)', textAlign: 'center', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                      {search ? `No transactions match "${search}"` : 'No transactions for this month'}
                    </td>
                  </tr>
                ) : filtered.map(t => (
                  <React.Fragment key={t.id}>
                    <tr onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                      style={{ borderTop: '1px solid var(--border)', transition: 'background 0.1s' }}>
                      <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                        <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelected(t.id)} />
                      </td>
                      <td style={{ padding: '9px 16px', color: 'var(--text-secondary)' }}>{t.date}</td>
                      <td style={{ padding: '9px 16px', color: 'var(--text-primary)' }}>{t.narration}</td>
                      <td style={{ padding: '9px 16px', color: 'var(--text-secondary)', textAlign: 'right' }}>{t.debit_amount ? `₹${fmt(t.debit_amount)}` : ''}</td>
                      <td style={{ padding: '9px 16px', color: 'var(--accent-green)', textAlign: 'right' }}>{t.credit_amount ? `₹${fmt(t.credit_amount)}` : ''}</td>
                      <td style={{ padding: '9px 16px', color: 'var(--text-muted)', textAlign: 'right' }}>₹{fmt(t.balance)}</td>
                      <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                        <ClassificationSelect value={t.classification} onChange={v => setClassification(t, v)} />
                      </td>
                      <td style={{ padding: '9px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button onClick={() => startEdit(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                            <Pencil size={13} color="var(--text-muted)" />
                          </button>
                          <button onClick={() => deleteOne(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                            <Trash2 size={13} color="var(--accent-red)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editingId === t.id && (
                      <tr>
                        <td colSpan={8} style={{ padding: '12px 16px', background: 'var(--bg-elevated)' }}>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input type="date" value={editDraft.date} onChange={e => setEditDraft(d => ({ ...d, date: e.target.value }))}
                              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12.5 }} />
                            <input type="text" placeholder="Narration" value={editDraft.narration} onChange={e => setEditDraft(d => ({ ...d, narration: e.target.value }))}
                              style={{ flex: '1 1 200px', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12.5 }} />
                            <input type="number" placeholder="Debit" value={editDraft.debit_amount} onChange={e => setEditDraft(d => ({ ...d, debit_amount: e.target.value }))}
                              style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12.5 }} />
                            <input type="number" placeholder="Credit" value={editDraft.credit_amount} onChange={e => setEditDraft(d => ({ ...d, credit_amount: e.target.value }))}
                              style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12.5 }} />
                            <input type="number" placeholder="Balance" value={editDraft.balance} onChange={e => setEditDraft(d => ({ ...d, balance: e.target.value }))}
                              style={{ width: 110, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12.5 }} />
                            <button disabled={savingEdit} onClick={() => saveEdit(t)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'var(--accent-green)', color: '#04231a', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Save</button>
                            <button onClick={cancelEdit} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CashFlow;
